import { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, Trash2, Crown, Shield, User as UserIcon, Mail, Loader2, X, Check, ChevronDown } from 'lucide-react';
import { teamsApi, TeamDto, TeamInvitation, TeamRole } from '../lib/teamsApi';
import { useAuth } from '../context/AuthContext';
import { useSubscriptionStatus } from '../context/SubscriptionContext';
import ConfirmDialog from './admin/ConfirmDialog';

interface Props {
  darkMode: boolean;
  cardClasses: string;
  inputClasses: string;
}

const ROLE_LABELS: Record<TeamRole, string> = {
  owner: 'Inhaber',
  admin: 'Admin',
  member: 'Mitglied',
};

const ROLE_ICONS: Record<TeamRole, React.ReactNode> = {
  owner: <Crown className="w-3 h-3" />,
  admin: <Shield className="w-3 h-3" />,
  member: <UserIcon className="w-3 h-3" />,
};

export default function TeamSettings({ darkMode, cardClasses, inputClasses }: Props) {
  const { user, refreshUser } = useAuth();
  const { status: subStatus } = useSubscriptionStatus();
  const [team, setTeam] = useState<TeamDto | null>(null);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviting, setSaving] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [actionPending, setActionPending] = useState<string | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<number | null>(null);
  const [leaveConfirm, setLeaveConfirm] = useState(false);

  const myRole = user?.teamRole ?? null;
  const isOwnerOrAdmin = myRole === 'owner' || myRole === 'admin';

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [t, inv] = await Promise.all([
        teamsApi.getMyTeam(),
        isOwnerOrAdmin ? teamsApi.listInvitations() : Promise.resolve([]),
      ]);
      setTeam(t);
      setInvitations(inv);
      setNameInput(t.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [isOwnerOrAdmin]);

  useEffect(() => { reload(); }, [reload]);

  const saveName = async () => {
    if (!nameInput.trim()) return;
    setSavingName(true);
    try {
      await teamsApi.updateName(nameInput.trim());
      await reload();
      setEditingName(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Speichern');
    } finally { setSavingName(false); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSaving(true);
    setInviteError('');
    try {
      await teamsApi.invite(inviteEmail.trim(), inviteRole);
      setInviteEmail('');
      setInviteOpen(false);
      await reload();
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : 'Fehler beim Einladen');
    } finally { setSaving(false); }
  };

  const handleRevoke = async (id: number) => {
    setActionPending(`revoke-${id}`);
    try { await teamsApi.revokeInvitation(id); await reload(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Fehler'); }
    finally { setActionPending(null); }
  };

  const handleRemove = (userId: number) => {
    setRemoveConfirm(userId);
  };

  const performRemove = async (userId: number) => {
    setRemoveConfirm(null);
    setActionPending(`remove-${userId}`);
    try { await teamsApi.removeMember(userId); await reload(); await refreshUser(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Fehler'); }
    finally { setActionPending(null); }
  };

  const handleLeave = () => {
    setLeaveConfirm(true);
  };

  const performLeave = async () => {
    setLeaveConfirm(false);
    setActionPending('leave');
    try { await teamsApi.leave(); await refreshUser(); window.location.href = '/'; }
    catch (e) { setError(e instanceof Error ? e.message : 'Fehler'); }
    finally { setActionPending(null); }
  };

  const handleChangeRole = async (userId: number, role: 'admin' | 'member') => {
    setActionPending(`role-${userId}`);
    try { await teamsApi.changeRole(userId, role); await reload(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Fehler'); }
    finally { setActionPending(null); }
  };

  const maxSeats = subStatus?.maxSeats ?? 1;
  const usedSeats = team?.members.length ?? 0;

  if (loading) return (
    <div className="flex justify-center p-8">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
    </div>
  );

  if (error) return (
    <div className={`${cardClasses} text-red-500 text-sm`}>
      Fehler: {error}
      <button onClick={reload} className="ml-2 underline">Erneut laden</button>
    </div>
  );

  if (!team) return null;

  const labelCls = `block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`;
  const mutedCls = darkMode ? 'text-slate-400' : 'text-slate-500';
  const borderCls = darkMode ? 'border-slate-700' : 'border-slate-100';

  return (
    <div className="space-y-6">
      {/* Team-Name */}
      <div className={cardClasses}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Team-Name
          </h3>
          {isOwnerOrAdmin && !editingName && (
            <button onClick={() => setEditingName(true)}
              className="text-xs text-blue-500 hover:text-blue-600">
              Bearbeiten
            </button>
          )}
        </div>
        {editingName ? (
          <div className="flex gap-2">
            <input value={nameInput} onChange={e => setNameInput(e.target.value)}
              className={`${inputClasses} flex-1`} maxLength={200} />
            <button onClick={saveName} disabled={savingName}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 flex items-center gap-1">
              {savingName ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Speichern
            </button>
            <button onClick={() => { setEditingName(false); setNameInput(team.name); }}
              className={`px-3 py-1.5 text-sm rounded-md ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <p className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>{team.name}</p>
        )}
      </div>

      {/* Mitglieder */}
      <div className={cardClasses}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Mitglieder
            </h3>
            <p className={`text-xs mt-0.5 ${mutedCls}`}>
              {usedSeats} von {maxSeats} Platz{maxSeats !== 1 ? 'en' : ''} belegt
            </p>
          </div>
          {isOwnerOrAdmin && (
            <button onClick={() => setInviteOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md">
              <UserPlus className="w-4 h-4" /> Einladen
            </button>
          )}
        </div>

        <div className="space-y-0">
          {team.members.map(m => (
            <div key={m.id}
              className={`flex items-center gap-3 py-3 border-b last:border-0 ${borderCls}`}>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {m.contactPerson || m.email}
                </p>
                <p className={`text-xs truncate ${mutedCls}`}>{m.email}</p>
              </div>
              {/* Rolle */}
              {m.id === user?.id || myRole !== 'owner' ? (
                <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                  m.teamRole === 'owner' ? (darkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-50 text-yellow-700') :
                  m.teamRole === 'admin' ? (darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700') :
                  (darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600')
                }`}>
                  {ROLE_ICONS[m.teamRole]} {ROLE_LABELS[m.teamRole]}
                </span>
              ) : (
                /* Owner kann Rollen ändern */
                <div className="relative">
                  <select
                    value={m.teamRole}
                    disabled={actionPending === `role-${m.id}`}
                    onChange={e => handleChangeRole(m.id, e.target.value as 'admin' | 'member')}
                    className={`text-xs rounded px-2 py-1 pr-6 appearance-none ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-white border-slate-200 text-slate-700'} border`}>
                    <option value="admin">Admin</option>
                    <option value="member">Mitglied</option>
                  </select>
                  <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-60" />
                </div>
              )}
              {/* Entfernen-Button (Owner/Admin, aber nicht für sich selbst und nicht für Owner) */}
              {isOwnerOrAdmin && m.id !== user?.id && m.teamRole !== 'owner' && (
                <button onClick={() => handleRemove(m.id)}
                  disabled={actionPending === `remove-${m.id}`}
                  className={`p-1 rounded hover:text-red-500 disabled:opacity-50 ${mutedCls}`}
                  title="Mitglied entfernen">
                  {actionPending === `remove-${m.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Team verlassen (nur für nicht-Owner) */}
        {myRole !== 'owner' && (
          <div className={`mt-4 pt-4 border-t ${borderCls}`}>
            <button onClick={handleLeave} disabled={actionPending === 'leave'}
              className="text-sm text-red-500 hover:text-red-600 disabled:opacity-50 flex items-center gap-1.5">
              {actionPending === 'leave' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Team verlassen
            </button>
          </div>
        )}
      </div>

      {/* Offene Einladungen */}
      {isOwnerOrAdmin && invitations.length > 0 && (
        <div className={cardClasses}>
          <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Offene Einladungen
          </h3>
          <div className="space-y-0">
            {invitations.map(inv => (
              <div key={inv.id}
                className={`flex items-center gap-3 py-2.5 border-b last:border-0 ${borderCls}`}>
                <Mail className={`w-4 h-4 flex-shrink-0 ${mutedCls}`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>{inv.email}</p>
                  <p className={`text-xs ${mutedCls}`}>{ROLE_LABELS[inv.role]} · Läuft ab: {new Date(inv.expiresAt).toLocaleDateString('de-DE')}</p>
                </div>
                <button onClick={() => handleRevoke(inv.id)}
                  disabled={actionPending === `revoke-${inv.id}`}
                  className={`p-1 rounded hover:text-red-500 disabled:opacity-50 ${mutedCls}`}
                  title="Einladung widerrufen">
                  {actionPending === `revoke-${inv.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={removeConfirm !== null}
        title="Mitglied entfernen"
        message="Mitglied wirklich entfernen? Ihre Berichte werden an den Inhaber übertragen."
        confirmLabel="Entfernen"
        onConfirm={() => removeConfirm !== null && performRemove(removeConfirm)}
        onCancel={() => setRemoveConfirm(null)}
      />

      <ConfirmDialog
        open={leaveConfirm}
        title="Team verlassen"
        message="Team wirklich verlassen?"
        confirmLabel="Verlassen"
        onConfirm={performLeave}
        onCancel={() => setLeaveConfirm(false)}
      />

      {/* Einladen-Modal */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`w-full max-w-md mx-4 rounded-xl shadow-xl p-6 ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Mitglied einladen
              </h3>
              <button onClick={() => { setInviteOpen(false); setInviteEmail(''); setInviteError(''); }}>
                <X className={`w-5 h-5 ${mutedCls}`} />
              </button>
            </div>
            <div className={`mb-4 rounded-lg p-3 text-sm ${darkMode ? 'bg-slate-700/60 text-slate-300' : 'bg-blue-50 text-slate-700'}`}>
              Die Person tritt deinem Team <strong>{team.name}</strong> bei.
              <button type="button"
                onClick={() => { setInviteOpen(false); setEditingName(true); setNameInput(team.name); }}
                className="ml-1 text-blue-500 hover:underline">Heißt es anders? Team-Name ändern</button>
            </div>
            {inviteError && (
              <p className="mb-3 text-sm text-red-500">{inviteError}</p>
            )}
            <div className="space-y-4">
              <div>
                <label className={labelCls}>E-Mail-Adresse</label>
                <input type="email" value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleInvite()}
                  placeholder="name@beispiel.de"
                  className={`${inputClasses} w-full`} />
              </div>
              <div>
                <label className={labelCls}>Rolle</label>
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value as 'admin' | 'member')}
                  className={`${inputClasses} w-full`}>
                  <option value="member">Mitglied</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}
                  className="flex-1 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Einladung senden
                </button>
                <button onClick={() => { setInviteOpen(false); setInviteEmail(''); setInviteError(''); }}
                  className={`px-4 py-2 text-sm rounded-lg ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
